// ============================================================
// XMO.01 D#3 — Prompt Injection Filter
//
// Runs on every patient chat message BEFORE forwarding to
// Nurse AI. Pattern matching only — no LLM in security loop.
//
// Spec: HLX-XMO.01, Section: Chat Injection
// Criteria:
//   c1 — Intercepts message before Nurse AI (callers must invoke before LLM call)
//   c2 — Covers injection templates, jailbreaks, encoded attacks, social engineering
//   c3 — Pattern set is in securityPatterns.ts (configurable, versioned)
//   c4 — Returns XMO_INJECTION_DETECTED + patient-safe message on detection
//   c5 — < 50ms target: all regexes pre-compiled at module load; early-exit on first match
// ============================================================

import { SECURITY_PATTERNS, PATTERN_VERSION, PATTERN_COUNT } from './securityPatterns';

/** Error code written to audit log on detection. Never shown to the patient. */
export const XMO_INJECTION_DETECTED = 'XMO_INJECTION_DETECTED' as const;

/**
 * Patient-facing message returned when injection is detected.
 * Must never reveal what was detected or why — generic redirect only.
 * Exact wording required by spec c4.
 */
export const INJECTION_PATIENT_MESSAGE =
  'I can only help with questions about your health and lab results.';

export interface InjectionScanResult {
  /** True if an injection pattern was detected */
  blocked: boolean;
  /** Error code for audit logging — only set when blocked */
  errorCode?: typeof XMO_INJECTION_DETECTED;
  /** Safe message to show the patient — only set when blocked */
  patientMessage?: string;
  /** Pattern ID that triggered detection — for audit logging, NOT for patient display */
  matchedPatternId?: string;
  /** Pattern category — for audit logging */
  category?: string;
  /** Pattern set version at time of scan */
  patternVersion: string;
  /** Scan duration in ms — for latency monitoring */
  scanMs: number;
}

/**
 * XMO.01 D#3 (c1–c5): Scan a patient chat message for prompt injection.
 *
 * Pure in-memory operation — no I/O, no LLM, no async.
 * All regexes are pre-compiled at module load.
 * Early-exits on first pattern match (fail-fast for latency — c5).
 *
 * Call this BEFORE forwarding any patient message to Nurse AI (c1).
 * If result.blocked === true, return result.patientMessage to the patient
 * and DO NOT forward the message to the LLM (c4).
 *
 * @param message — raw patient message text
 * @returns InjectionScanResult with blocked=true if injection detected
 */
export function scanForInjection(message: string): InjectionScanResult {
  const start = performance.now();

  // Guard: empty or whitespace-only messages are safe — no scan needed
  if (!message || !message.trim()) {
    return {
      blocked: false,
      patternVersion: PATTERN_VERSION,
      scanMs: performance.now() - start,
    };
  }

  // Run each pre-compiled pattern — early exit on first match (c5 latency)
  for (const pattern of SECURITY_PATTERNS) {
    if (pattern.regex.test(message)) {
      const scanMs = performance.now() - start;
      return {
        blocked: true,
        errorCode: XMO_INJECTION_DETECTED,      // c4: error code
        patientMessage: INJECTION_PATIENT_MESSAGE, // c4: patient-safe message
        matchedPatternId: pattern.id,
        category: pattern.category,
        patternVersion: PATTERN_VERSION,
        scanMs,
      };
    }
  }

  return {
    blocked: false,
    patternVersion: PATTERN_VERSION,
    scanMs: performance.now() - start,
  };
}

/**
 * Build an audit log entry for an injection detection event.
 *
 * IMPORTANT: Logs the pattern ID and category that fired, but NOT the
 * message content. Message content is never persisted (c4 scrubbing).
 *
 * @param result — result from scanForInjection (must have blocked=true)
 * @param sessionId — session identifier for correlation
 */
export function buildInjectionAuditLog(
  result: InjectionScanResult,
  sessionId: string,
): Record<string, unknown> {
  return {
    eventType: 'injection_detected',
    errorCode: result.errorCode,
    matchedPatternId: result.matchedPatternId,
    category: result.category,
    patternVersion: result.patternVersion,
    patternCount: PATTERN_COUNT,
    scanMs: result.scanMs,
    sessionId,
    timestamp: new Date().toISOString(),
    // NOTE: message content is intentionally NOT logged here (privacy + c4 scrubbing)
  };
}
